import { useState, type ReactNode } from 'react';
import {
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  type CollisionDetection,
} from '@dnd-kit/core';
import { Botao, juntarClasses } from '../../componentes/ui';

/**
 * A grade de mês dos calendários do CRM — o do Pipeline (cards pela data de
 * execução) e o de Atividades (tarefas pelo prazo). Um lugar só para a conta
 * dos dias, a navegação, a célula do dia e o arrastar, para os dois não
 * divergirem.
 */

export const DIAS_DA_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const;

export interface MesDoCalendario {
  readonly ano: number;
  /** 0 = janeiro, como no `Date`. */
  readonly mes: number;
}

export interface DiaDaGrade {
  readonly iso: string;
  readonly dia: number;
  /** `false` nas pontas dos meses vizinhos — aparecem apagados. */
  readonly doMes: boolean;
}

/** `AAAA-MM-DD` a partir de partes locais — sem `toISOString`, que converte para UTC. */
export function isoDoDia(ano: number, mes: number, dia: number): string {
  return `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/**
 * Seis semanas sempre: 42 dias, o mês e as pontas dos vizinhos, de domingo a
 * sábado. A altura não pula ao trocar de mês.
 */
export function diasDaGrade(mes: MesDoCalendario): DiaDaGrade[] {
  const inicio = new Date(mes.ano, mes.mes, 1);
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(mes.ano, mes.mes, 1 - inicio.getDay() + i);
    return {
      iso: isoDoDia(d.getFullYear(), d.getMonth(), d.getDate()),
      dia: d.getDate(),
      doMes: d.getMonth() === mes.mes,
    };
  });
}

/** "outubro de 2026". */
export function tituloDoMes(mes: MesDoCalendario): string {
  return new Date(mes.ano, mes.mes, 1).toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  });
}

function mesDeHoje(): MesDoCalendario {
  const agora = new Date();
  return { ano: agora.getFullYear(), mes: agora.getMonth() };
}

/** O mês exibido, com o avançar, o voltar e o "Hoje". */
export function useMesDoCalendario(): {
  readonly mes: MesDoCalendario;
  readonly mudar: (delta: number) => void;
  readonly irParaHoje: () => void;
} {
  const [mes, setMes] = useState(mesDeHoje);
  return {
    mes,
    mudar: (delta) => {
      const d = new Date(mes.ano, mes.mes + delta, 1);
      setMes({ ano: d.getFullYear(), mes: d.getMonth() });
    },
    irParaHoje: () => setMes(mesDeHoje()),
  };
}

/** ‹ Hoje › — o mesmo trio nos dois calendários. */
export function NavegacaoDoMes(props: {
  readonly aoMudar: (delta: number) => void;
  readonly aoIrParaHoje: () => void;
}): JSX.Element {
  return (
    <div className="flex gap-2">
      <Botao
        variante="secundario"
        tamanho="pequeno"
        onClick={() => props.aoMudar(-1)}
        aria-label="Mês anterior"
      >
        ‹
      </Botao>
      <Botao variante="secundario" tamanho="pequeno" onClick={props.aoIrParaHoje}>
        Hoje
      </Botao>
      <Botao
        variante="secundario"
        tamanho="pequeno"
        onClick={() => props.aoMudar(1)}
        aria-label="Próximo mês"
      >
        ›
      </Botao>
    </div>
  );
}

// ─── Arrastar ───────────────────────────────────────────────────────────────

/** O alvo de soltura de cada dia: `dia:AAAA-MM-DD`. */
export const PREFIXO_DO_DIA = 'dia:';

/**
 * Com o mouse ou o dedo, vale a célula sob o ponteiro; pelo teclado não há
 * ponteiro, e vale a célula que o item mais cobre.
 */
export const colisaoDoCalendario: CollisionDetection = (args) => {
  const sobOPonteiro = pointerWithin(args);
  return sobOPonteiro.length > 0 ? sobOPonteiro : rectIntersection(args);
};

/** A célula de um dia: recebe o que se solta nela e destaca enquanto algo passa por cima. */
export function CelulaDoDia(props: {
  readonly iso: string;
  readonly dia: number;
  readonly doMes: boolean;
  readonly hoje: boolean;
  readonly bordaDireita: boolean;
  readonly bordaInferior: boolean;
  readonly children: ReactNode;
}): JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: `${PREFIXO_DO_DIA}${props.iso}` });

  return (
    <div
      ref={setNodeRef}
      className={juntarClasses(
        'flex min-h-28 flex-col gap-1 border-borda p-1.5 transition-colors',
        props.bordaDireita && 'border-r',
        props.bordaInferior && 'border-b',
        isOver
          ? 'bg-acento-ciano/10 ring-2 ring-inset ring-acento-ciano/50'
          : props.doMes
            ? 'bg-superficie-elevada'
            : 'bg-superficie-base/60',
      )}
    >
      <span
        className={juntarClasses(
          'flex h-6 w-6 items-center justify-center self-end rounded-full text-xs tabular-nums',
          props.hoje
            ? 'bg-acento-indigo font-semibold text-white'
            : props.doMes
              ? 'text-conteudo-secundario'
              : 'text-conteudo-sutil/60',
        )}
        {...(props.hoje ? { 'aria-label': `Hoje, ${props.dia}` } : {})}
      >
        {props.dia}
      </span>
      <div className="flex max-h-32 flex-col gap-1 overflow-y-auto">{props.children}</div>
    </div>
  );
}

/** O item pego pelo calendário: o original fica apagado enquanto a cópia voa. */
export function Arrastavel(props: {
  readonly id: string;
  /** Como o leitor de tela chama o item — "card arrastável", "atividade arrastável". */
  readonly descricao: string;
  readonly children: ReactNode;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: props.id });

  return (
    <div
      ref={setNodeRef}
      className={`cursor-grab active:cursor-grabbing ${isDragging ? 'opacity-40' : ''}`}
      {...attributes}
      {...listeners}
      aria-roledescription={props.descricao}
    >
      {props.children}
    </div>
  );
}
